import { ApiResult, HttpClient } from "../http.js";
import {
  PagedQuotes,
  QuoteDiscountType,
  QuoteDto,
  QuoteNoteDto,
  QuoteNoteVisibility,
  QuotePdfLinkDto,
  QuoteStatus,
  SetQuoteEstimationPayload,
  SetQuoteLetterContentPayload,
} from "../types/quotes.js";

/**
 * Optional header / pricing fields carried on create &amp; update for 1:1 parity with the
 * legacy Firestore quote document. Pricing scalars (subtotal / vatAmount / total / discount*)
 * are stored verbatim — the frontend computes them client-side.
 */
export interface QuoteHeaderDetailsPayload {
  companyName?: string | null;
  description?: string | null;
  customerCity?: string | null;
  customerZip?: string | null;
  customerCvr?: string | null;
  subtotal?: number | null;
  vatAmount?: number | null;
  total?: number | null;
  discountType?: QuoteDiscountType | null;
  discountValue?: number | null;
  discountAmount?: number | null;
  estimatedHours?: number | null;
  totalHours?: number | null;
  totalLaborCost?: number | null;
  termsAndConditions?: string | null;
  paymentTerms?: string | null;
  internalNotes?: string | null;
  customerNotes?: string | null;
  parentProjectId?: string | null;
  parentProjectTitle?: string | null;
  parentCaseNumber?: string | null;
  rejectionReason?: string | null;
  acceptToken?: string | null;
  viewCount?: number | null;
  createdById?: string | null;
  createdByName?: string | null;
  attachmentUrls?: string[] | null;
}

/**
 * One line item in a whole-array replacement on update. `lineTotal` is the client-computed
 * total, stored verbatim.
 */
export interface QuoteLineItemInputPayload {
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  vatRate?: number | null;
  productId?: string | null;
  /** Loose link to the price library item the line was built from. Send it back on every edit, or the link is lost. */
  priceLibraryItemId?: string | null;
  externalRef?: string | null;
  lineTotal?: number | null;
  discount?: number | null;
  discountType?: QuoteDiscountType | null;
  category?: string | null;
}

export interface CreateQuotePayload {
  title: string;
  /**
   * Omit (or send blank) to have the server allocate the next number from the
   * org's quote-number sequence — see Settings → Numre & fakturering.
   */
  quoteNumber?: string | null;
  customerId?: string | null;
  customerName?: string | null;
  customerAddress?: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;
  validUntil?: string | null;
  currency?: string | null;
  vatRate?: number | null;
  id?: string;
  details?: QuoteHeaderDetailsPayload | null;
}

export interface UpdateQuotePayload {
  title: string;
  customerName?: string | null;
  customerAddress?: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;
  validUntil?: string | null;
  currency?: string | null;
  vatRate?: number | null;
  details?: QuoteHeaderDetailsPayload | null;
  /** Whole-array line-item replacement (legacy frontend write shape). Omit to leave untouched. */
  lineItems?: QuoteLineItemInputPayload[] | null;
}

export interface ListQuotesQuery {
  page?: number;
  pageSize?: number;
  /** Matched server-side on Title, QuoteNumber, CustomerName and CustomerCvr. */
  search?: string;
  /** Narrows the list to one customer's quotes (server-side — replaces draining every quote and filtering client-side). */
  customerId?: string;
  /**
   * Only these statuses. Integer wire values of the .NET `QuoteStatus` enum (0 Draft · 1 Sent ·
   * 2 Accepted · 3 Rejected · 4 Expired · 5 Converted · 6 Viewed · 7 Won · 8 Lost) — map UI
   * tokens with `mapModelStatusToApi`/the app's equivalent.
   */
  statuses?: readonly number[];
  /** Users without quotes.viewAll: only quotes this user created or participates in. */
  visibleToUserId?: string;
  /** "Medarbejder" filter: quotes created by, or participated in by, any of these users. */
  userIds?: readonly string[];
  /** Inclusive range on the displayed total (Total, else TotalInclVat). */
  minTotal?: number;
  maxTotal?: number;
  /** Only quotes no besigtigelse (walkthrough) links to. */
  unlinkedOnly?: boolean;
  /** Omitted = the server default (updatedAt desc). Anything outside this set is a 400. */
  sortBy?: QuoteListSortBy;
  sortDescending?: boolean;
}

export type QuoteListSortBy = "quoteNumber" | "title" | "customerName" | "total" | "validUntil" | "createdAt";

export interface ChangeQuoteStatusPayload {
  targetStatus: QuoteStatus;
  projectId?: string | null;
}

export interface DuplicateQuotePayload {
  /** Omit to allocate the next number from the org's quote-number sequence. */
  newQuoteNumber?: string | null;
}

export interface SendQuotePayload {
  recipientEmail: string;
  subject: string;
  message: string;
}

/**
 * Optional per-send overrides for the follow-up reminder. Omit both (or the whole payload)
 * for the one-click case: the org's saved follow-up text under Indstillinger is used.
 */
export interface SendQuoteFollowUpPayload {
  subject?: string;
  message?: string;
}

export interface SyncWalkthroughToQuotePayload {
  walkthroughId: string;
}

export interface AddQuoteLineItemPayload {
  description: string;
  quantity: number;
  unit?: string | null;
  unitPrice: number;
  vatRate?: number | null;
  productId?: string | null;
  priceLibraryItemId?: string | null;
  externalRef?: string | null;
  category?: string | null;
}

export interface AddQuoteTermsBlockPayload {
  content: string;
}

/** Body for `PUT /api/quotes/{id}/line-items/order`: every line-item id exactly once, in the new order. */
export interface ReorderQuoteLineItemsPayload {
  orderedIds: string[];
}

export interface EnableWorkspacePayload {
  creatorName: string;
  creatorEmail: string;
}

export interface SetTenderOutcomePayload {
  /** `"won"` or `"lost"`. */
  outcome: "won" | "lost";
  reason?: string | null;
  markHoursBillable: boolean;
}

export interface RejectQuotePayload {
  isWorkspace: boolean;
  rejectionReason?: string | null;
}

/** Result of a reject: the resulting quote plus the count of cascaded time entries. */
export interface RejectQuoteResult {
  quote: QuoteDto;
  timeEntriesUpdated: number;
}

/** Body for `PUT /api/quotes/{id}/internal-notes`. Null or blank clears the notes. */
export interface SetQuoteInternalNotesPayload {
  internalNotes: string | null;
}

/** How the accepted quote relates to a project. */
export type AcceptProjectMode = "none" | "new" | "subproject";

export interface AcceptQuotePayload {
  createProject: AcceptProjectMode;
  parentProjectId?: string | null;
  migrateDocuments?: boolean;
  linkTimeEntries?: boolean;
  /** Copy the quote's kalkulation onto the project. Defaults to true server-side. */
  migrateCalculation?: boolean;
  /** Carry the quote's interne noter into the project's noter. Defaults to true server-side. */
  migrateNotes?: boolean;
}

/** Result of an accept: the resulting quote and the created project id (if any). */
export interface AcceptQuoteResult {
  quote: QuoteDto;
  projectId: string | null;
}

export interface ConvertQuotePayload {
  createProject: AcceptProjectMode;
  parentProjectId?: string | null;
  migrateDocuments?: boolean;
  linkTimeEntries?: boolean;
  /** Copy the quote's kalkulation onto the project. Defaults to true server-side. */
  migrateCalculation?: boolean;
  /** Carry the quote's interne noter into the project's noter. Defaults to true server-side. */
  migrateNotes?: boolean;
}

export interface BulkUpdateTimeEntryBillablePayload {
  billable: boolean;
}

/**
 * Adds a collaboration note to a quote. The author (userId / userName) is stamped
 * server-side from the authenticated caller, so only the content + optional
 * visibility are sent. `visibility` defaults to `"internal"` server-side when omitted.
 */
export interface AddQuoteNotePayload {
  content: string;
  visibility?: QuoteNoteVisibility;
}

export interface UpdateQuoteNotePayload {
  content: string;
}

export class QuotesApi {
  constructor(private readonly http: HttpClient) {}

  list(query: ListQuotesQuery = {}): Promise<ApiResult<PagedQuotes>> {
    return this.http.get<PagedQuotes>("/api/quotes", { query: { ...query } });
  }

  get(id: string): Promise<ApiResult<QuoteDto>> {
    return this.http.get<QuoteDto>(`/api/quotes/${encodeURIComponent(id)}`);
  }

  create(payload: CreateQuotePayload): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/quotes", { body: payload });
  }

  update(id: string, payload: UpdateQuotePayload): Promise<ApiResult<QuoteDto>> {
    return this.http.put<QuoteDto>(`/api/quotes/${encodeURIComponent(id)}`, { body: payload });
  }

  /**
   * Persists (or clears) the quote's cover letter. Separate from `update` because the letter
   * editor saves on its own without re-sending the required header fields (e.g. title).
   */
  setLetterContent(
    id: string,
    payload: SetQuoteLetterContentPayload,
  ): Promise<ApiResult<QuoteDto>> {
    return this.http.put<QuoteDto>(
      `/api/quotes/${encodeURIComponent(id)}/letter-content`,
      { body: payload },
    );
  }

  /**
   * Persists (or clears) the operator's private working notes — the free-text scratchpad on
   * the quote's Detaljer tab. Separate from `update` because that PUT replaces the whole
   * details object (a notes-only save through it would blank the other free-text fields and
   * zero the stored totals), and because it stays writable on a locked / accepted quote.
   */
  setInternalNotes(
    id: string,
    payload: SetQuoteInternalNotesPayload,
  ): Promise<ApiResult<QuoteDto>> {
    return this.http.put<QuoteDto>(
      `/api/quotes/${encodeURIComponent(id)}/internal-notes`,
      { body: payload },
    );
  }

  /**
   * Persists (or clears) the quote's estimation. Separate from `update` because the estimation
   * tab saves on its own without re-sending the required header fields (e.g. title). The
   * combined header totals ride along because the estimation feeds the quote price.
   */
  setEstimation(
    id: string,
    payload: SetQuoteEstimationPayload,
  ): Promise<ApiResult<QuoteDto>> {
    return this.http.put<QuoteDto>(
      `/api/quotes/${encodeURIComponent(id)}/estimation`,
      { body: payload },
    );
  }

  remove(id: string): Promise<ApiResult<boolean>> {
    return this.http.delete<boolean>(`/api/quotes/${encodeURIComponent(id)}`);
  }

  changeStatus(id: string, payload: ChangeQuoteStatusPayload): Promise<ApiResult<QuoteDto>> {
    return this.http.post<QuoteDto>(`/api/quotes/${encodeURIComponent(id)}/status`, { body: payload });
  }

  duplicate(id: string, payload: DuplicateQuotePayload): Promise<ApiResult<QuoteDto>> {
    return this.http.post<QuoteDto>(`/api/quotes/${encodeURIComponent(id)}/duplicate`, { body: payload });
  }

  /**
   * Re-projects the linked walkthrough's items into the quote, replacing any existing
   * walkthrough-sourced line items (tagged `walkthrough:{walkthroughId}`) while leaving
   * manually-added items untouched. Returns the full updated quote. Replaces the
   * decommissioned Firebase Cloud Function `syncWalkthroughToQuote`.
   */
  syncFromWalkthrough(id: string, payload: SyncWalkthroughToQuotePayload): Promise<ApiResult<QuoteDto>> {
    return this.http.post<QuoteDto>(`/api/quotes/${encodeURIComponent(id)}/sync-from-walkthrough`, { body: payload });
  }

  /**
   * Generates the quote PDF server-side, emails it to the recipient (honouring per-org email
   * test mode) and bumps the quote to `Sent`. Replaces the decommissioned Firebase Cloud
   * Function `sendQuoteWithPdf`.
   */
  send(id: string, payload: SendQuotePayload): Promise<ApiResult<QuoteDto>> {
    return this.http.post<QuoteDto>(`/api/quotes/${encodeURIComponent(id)}/send`, { body: payload });
  }

  /**
   * Sends the follow-up reminder ("har I taget stilling til tilbuddet?") for a quote that has
   * been out for at least `FOLLOW_UP_AFTER_DAYS` days without an answer, and stamps
   * `followUpSentAt`. The server re-checks that window, so a stale list cannot mail the
   * customer twice.
   */
  sendFollowUp(id: string, payload: SendQuoteFollowUpPayload = {}): Promise<ApiResult<QuoteDto>> {
    return this.http.post<QuoteDto>(`/api/quotes/${encodeURIComponent(id)}/follow-up`, {
      body: payload,
    });
  }

  addLineItem(id: string, payload: AddQuoteLineItemPayload): Promise<ApiResult<QuoteDto>> {
    return this.http.post<QuoteDto>(`/api/quotes/${encodeURIComponent(id)}/line-items`, { body: payload });
  }

  removeLineItem(id: string, lineItemId: string): Promise<ApiResult<boolean>> {
    return this.http.delete<boolean>(
      `/api/quotes/${encodeURIComponent(id)}/line-items/${encodeURIComponent(lineItemId)}`,
    );
  }

  /** Replaces one line item in place (single-line save; does not touch the rest of the quote). Lock-guarded server-side. */
  updateLineItem(
    id: string,
    lineItemId: string,
    payload: AddQuoteLineItemPayload,
  ): Promise<ApiResult<QuoteDto>> {
    return this.http.put<QuoteDto>(
      `/api/quotes/${encodeURIComponent(id)}/line-items/${encodeURIComponent(lineItemId)}`,
      { body: payload },
    );
  }

  /** Persists the new line-item order. Needs the server's line-items/order endpoint (B3). */
  reorderLineItems(id: string, payload: ReorderQuoteLineItemsPayload): Promise<ApiResult<QuoteDto>> {
    return this.http.put<QuoteDto>(`/api/quotes/${encodeURIComponent(id)}/line-items/order`, {
      body: payload,
    });
  }

  addTermsBlock(id: string, payload: AddQuoteTermsBlockPayload): Promise<ApiResult<QuoteDto>> {
    return this.http.post<QuoteDto>(`/api/quotes/${encodeURIComponent(id)}/terms-blocks`, { body: payload });
  }

  removeTermsBlock(id: string, termsBlockId: string): Promise<ApiResult<boolean>> {
    return this.http.delete<boolean>(
      `/api/quotes/${encodeURIComponent(id)}/terms-blocks/${encodeURIComponent(termsBlockId)}`,
    );
  }

  /** Edits one terms block's content in place. Lock-guarded server-side. */
  updateTermsBlock(
    id: string,
    termsBlockId: string,
    payload: AddQuoteTermsBlockPayload,
  ): Promise<ApiResult<QuoteDto>> {
    return this.http.put<QuoteDto>(
      `/api/quotes/${encodeURIComponent(id)}/terms-blocks/${encodeURIComponent(termsBlockId)}`,
      { body: payload },
    );
  }

  /**
   * Renders the quote PDF for preview and returns a signed download link. No mutation, no
   * status change. Needs the server's pdf endpoint (B2).
   */
  getPdfLink(id: string): Promise<ApiResult<QuotePdfLinkDto>> {
    return this.http.get<QuotePdfLinkDto>(`/api/quotes/${encodeURIComponent(id)}/pdf`);
  }

  /**
   * Switches a quote into bid-workspace mode and creates the caller as the owner
   * participant, atomically (server-side transaction).
   */
  enableWorkspace(id: string, payload: EnableWorkspacePayload): Promise<ApiResult<QuoteDto>> {
    return this.http.post<QuoteDto>(`/api/quotes/${encodeURIComponent(id)}/enable-workspace`, { body: payload });
  }

  /**
   * Records the bid-workspace tender outcome (won/lost), locks the quote and cascades the
   * outcome onto its linked time entries, atomically.
   */
  setTenderOutcome(id: string, payload: SetTenderOutcomePayload): Promise<ApiResult<QuoteDto>> {
    return this.http.post<QuoteDto>(`/api/quotes/${encodeURIComponent(id)}/tender-outcome`, { body: payload });
  }

  /**
   * Rejects (or loses, for a workspace quote) the quote, locks it and marks its linked time
   * entries as a lost tender, atomically.
   */
  reject(id: string, payload: RejectQuotePayload): Promise<ApiResult<RejectQuoteResult>> {
    return this.http.post<RejectQuoteResult>(`/api/quotes/${encodeURIComponent(id)}/reject`, { body: payload });
  }

  /**
   * Accepts the quote, optionally converting it into a (sub)project, re-pointing its
   * documents/folders to the project and linking its time entries — all in one transaction.
   */
  accept(id: string, payload: AcceptQuotePayload): Promise<ApiResult<AcceptQuoteResult>> {
    return this.http.post<AcceptQuoteResult>(`/api/quotes/${encodeURIComponent(id)}/accept`, { body: payload });
  }

  /**
   * Converts an already accepted/won quote into a (sub)project, re-pointing its documents and
   * linking its time entries — in one transaction. Use this for the "Konverter til projekt"
   * action on an accepted/won quote (vs. {@link accept} which acts on a not-yet-accepted quote).
   */
  convert(id: string, payload: ConvertQuotePayload): Promise<ApiResult<AcceptQuoteResult>> {
    return this.http.post<AcceptQuoteResult>(`/api/quotes/${encodeURIComponent(id)}/convert`, { body: payload });
  }

  /** Bulk-sets the billable flag on every time entry linked to the quote, atomically. */
  bulkUpdateTimeEntryBillable(
    id: string,
    payload: BulkUpdateTimeEntryBillablePayload,
  ): Promise<ApiResult<number>> {
    return this.http.post<number>(
      `/api/quotes/${encodeURIComponent(id)}/time-entries/bulk-billable`,
      { body: payload },
    );
  }

  listNotes(id: string): Promise<ApiResult<QuoteNoteDto[]>> {
    return this.http.get<QuoteNoteDto[]>(`/api/quotes/${encodeURIComponent(id)}/notes`);
  }

  addNote(id: string, payload: AddQuoteNotePayload): Promise<ApiResult<QuoteNoteDto>> {
    return this.http.post<QuoteNoteDto>(`/api/quotes/${encodeURIComponent(id)}/notes`, { body: payload });
  }

  updateNote(
    id: string,
    noteId: string,
    payload: UpdateQuoteNotePayload,
  ): Promise<ApiResult<QuoteNoteDto>> {
    return this.http.put<QuoteNoteDto>(
      `/api/quotes/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}`,
      { body: payload },
    );
  }

  removeNote(id: string, noteId: string): Promise<ApiResult<boolean>> {
    return this.http.delete<boolean>(
      `/api/quotes/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}`,
    );
  }
}
